import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, SignOut, List, X, Factory } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import StonLogo from "@/components/StonLogo";
import { formatDateTime } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NOTIFICATION_POLL_INTERVAL_MS = 15000;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifs(res.data);
    } catch (e) {
      console.error("[Layout] fetchNotifs failed:", e);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    const t = setInterval(fetchNotifs, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user, fetchNotifs]);

  const unreadCount = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    fetchNotifs();
  };

  const onNotifClick = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.notif_id}/read`);
      fetchNotifs();
    }
    navigate(`/orders/${n.order_id}`);
  };

  const navItems = isAdmin
    ? [
        { to: "/admin", label: "Orders" },
        { to: "/admin/quotations", label: "Quotes" },
        { to: "/admin/products", label: "Products" },
        { to: "/admin/categories", label: "Categories" },
        { to: "/admin/comparison", label: "Compare" },
        { to: "/admin/companies", label: "Companies" },
        { to: "/admin/clients", label: "Clients" },
        { to: "/admin/new", label: "New Order" },
      ]
    : [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-white/40 supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <Link to={isAdmin ? "/admin" : "/home"} data-testid="brand-link">
            <StonLogo size={24} subtitle={isAdmin ? "Factory Console" : "Building Materials"} className="text-zinc-900" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="notifications-button"
                  className="relative p-2 hover:bg-zinc-100 transition-colors duration-200"
                >
                  <Bell size={20} weight="regular" className="text-zinc-700" />
                  {unreadCount > 0 && (
                    <span
                      data-testid="notifications-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#FF5A1F] text-white text-[10px] font-bold rounded-full grid place-items-center"
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 rounded-none border-zinc-200">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span className="mt-tiny">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      data-testid="mark-all-read-button"
                      className="text-[11px] text-[#0047AB] hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="p-6 text-center text-zinc-500 text-sm">No notifications</div>
                  ) : (
                    notifs.map((n) => (
                      <DropdownMenuItem
                        key={n.notif_id}
                        onClick={() => onNotifClick(n)}
                        data-testid={`notification-item-${n.notif_id}`}
                        className={`flex flex-col items-start gap-1 py-3 cursor-pointer ${
                          !n.read ? "bg-blue-50/50" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {!n.read && <span className="w-2 h-2 bg-[#FF5A1F] rounded-full" />}
                          <span className="text-sm font-medium text-zinc-900 flex-1">{n.message}</span>
                        </div>
                        <span className="mt-tiny text-zinc-400">{formatDateTime(n.created_at)}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="hidden md:flex items-center gap-2 px-3 py-2 hover:bg-zinc-100 transition-colors"
                >
                  <div className="w-7 h-7 bg-[#0047AB] text-white grid place-items-center font-bold text-xs uppercase">
                    {user?.name?.[0] || "?"}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium leading-none text-zinc-900">{user?.name}</div>
                    <div className="mt-tiny text-zinc-400 mt-0.5">{isAdmin ? "Admin" : "Client"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-zinc-200 min-w-[220px]">
                <DropdownMenuLabel>
                  <div className="text-sm text-zinc-900 font-medium">{user?.name}</div>
                  <div className="text-xs text-zinc-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="cursor-pointer">
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 hover:bg-zinc-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileOpen ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <div className="px-4 py-3 flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium ${
                      isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button
                onClick={logout}
                className="text-left px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
                data-testid="mobile-logout-button"
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1400px] mx-auto px-3 md:px-6 py-5 md:py-8">
        <div className="bg-[#f7f1e6]/97 backdrop-blur-sm rounded-[14px] shadow-[0_24px_70px_-24px_rgba(0,0,0,0.6)] border border-white/50 p-4 md:p-8 min-h-[70vh]">
          {children}
        </div>
      </main>

      <footer className="mt-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex items-center justify-between mt-tiny text-white/70">
          <div>© 2026 Ston Link — Building Materials</div>
          <div className="hidden sm:flex items-center gap-2">
            <Factory size={14} /> Curated · Certified · Delivered
          </div>
        </div>
      </footer>
    </div>
  );
}
